import { useEffect } from 'react'
import { useUpdateTask } from './useTasks.ts'
import type { Priority } from '../lib/types.ts'

interface HotkeyOptions {
  onNew?: () => void
  onEscape?: () => void
  /** Task the shortcuts act on: the open drawer, when there is one (ui-spec §8). */
  openTaskId?: number | null
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.isContentEditable ||
    target.tagName === 'SELECT'
  )
}

/**
 * Keyboard first (ui-spec §8): `n` new task, `/` search, `1 2 3` priority, `Esc` close.
 * Every shortcut is inert while the user is typing — otherwise a letter typed into a title would
 * fire an action.
 *
 * `e` (archive) deliberately lives in TaskDrawer instead: it opens a confirmation, and the
 * confirmation belongs to the component that owns the task being archived.
 */
export function useHotkeys({ onNew, onEscape, openTaskId = null }: HotkeyOptions): void {
  const updateTask = useUpdateTask()

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onEscape?.()
        return
      }

      if (isTyping(event.target) || event.metaKey || event.ctrlKey || event.altKey) return

      switch (event.key) {
        case 'n':
          event.preventDefault()
          onNew?.()
          break
        case '/': {
          event.preventDefault()
          // The view's own search wins over the top bar's, and a field CSS has hidden is skipped.
          // Querying `input[type="search"]` returned the first one in the DOM — always the top bar,
          // even on /table where the field that actually filters sits a few hundred pixels lower,
          // and below 700 px where the top bar field is `display: none` and nothing happened at all.
          const visible = (el: HTMLInputElement | null): el is HTMLInputElement =>
            el !== null && el.offsetParent !== null
          const view = document.querySelector<HTMLInputElement>('[data-hotkey-search="view"]')
          const global = document.querySelector<HTMLInputElement>('[data-hotkey-search="global"]')
          if (visible(view)) view.focus()
          else if (visible(global)) global.focus()
          break
        }
        case '1':
        case '2':
        case '3':
          if (openTaskId !== null) {
            event.preventDefault()
            updateTask.mutate({
              id: openTaskId,
              patch: { priority: Number(event.key) as Priority },
            })
          }
          break
        default:
          break
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onNew, onEscape, openTaskId, updateTask])
}
